/**
 * SfxPlayer — fire-and-forget one-shot voices.
 *
 * Handles the things naive `new Audio()` spam gets wrong: per-sound cooldowns
 * (no machine-gun retrigger), per-id and global voice caps (steal the oldest),
 * pitch variety, and 3D panning with distance cull. Positions arrive already in
 * RENDER space (the facade rebased them by the floating origin); the panner and
 * the listener therefore share a frame and spatialise correctly.
 *
 * BufferSources are one-shot by spec, so there's no pooling to do — we just cap
 * how many run at once and let `onended` reap them.
 */
import { SOUNDS, type SfxDef } from "./manifest";
import type { AudioEngine } from "./engine";
import type { SoundBank } from "./bank";

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface PlayOpts {
  /** Render-space position. Omit for a 2D (non-positional) sound. */
  pos?: Vec3;
  /** Distance from the listener (m); used for the cull check. */
  dist?: number;
  gain?: number;
  rate?: number;
}

interface Voice {
  id: string;
  src: AudioBufferSourceNode;
  out: GainNode;
  panner?: PannerNode;
  started: number;
}

const GLOBAL_CAP = 32;

export class SfxPlayer {
  private active: Voice[] = [];
  private retiring = new Set<Voice>();
  private lastPlay = new Map<string, number>();

  constructor(private engine: AudioEngine, private bank: SoundBank) {}

  play(id: string, opts: PlayOpts = {}): void {
    const def = SOUNDS[id];
    if (!def) {
      if (import.meta.env.DEV) console.warn(`[audio] play() unknown SFX "${id}"`);
      return;
    }
    const ctx = this.engine.ctx;
    const now = ctx.currentTime;

    // Cooldown — but only "spend" it once the sound actually plays.
    const cd = (def.cooldownMs ?? 0) / 1000;
    if (cd > 0 && now - (this.lastPlay.get(id) ?? -1e9) < cd) return;

    // Distance cull before we touch the graph.
    const spatial = !!def.spatial && !!opts.pos;
    if (spatial && opts.dist != null && opts.dist > (def.maxDist ?? 800) * 1.05) return;

    const buf = this.bank.get(id);
    if (!buf) return; // not decoded yet — skip this trigger (loading kicked off)
    this.lastPlay.set(id, now);

    this.evict(id, def);

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = (opts.rate ?? def.rate ?? 1) * (1 + (Math.random() * 2 - 1) * (def.rateVar ?? 0));

    const vGain = ctx.createGain();
    vGain.gain.value = (def.gain ?? 1) * (opts.gain ?? 1);

    let panner: PannerNode | undefined;
    if (spatial && opts.pos) {
      panner = ctx.createPanner();
      panner.panningModel = "equalpower"; // cheap; HRTF not worth it at this voice count
      panner.distanceModel = "inverse";
      panner.refDistance = def.refDist ?? 10;
      panner.maxDistance = def.maxDist ?? 1000;
      panner.rolloffFactor = def.rolloff ?? 1;
      setPannerPos(panner, opts.pos);
      src.connect(panner).connect(vGain);
    } else {
      src.connect(vGain);
    }
    vGain.connect(this.engine.busInput(def.bus ?? "sfx"));

    const voice: Voice = { id, src, out: vGain, panner, started: now };
    this.active.push(voice);
    src.onended = () => this.reap(voice);
    src.start();
  }

  /** Make room: enforce the sound's id/group cap, then the global cap (steal oldest). */
  private evict(id: string, def: SfxDef): void {
    const cap = def.maxVoices ?? 6;
    const same = this.active.filter((v) => def.voiceGroup ? SOUNDS[v.id]?.voiceGroup === def.voiceGroup : v.id === id);
    for (let i = 0; i <= same.length - cap; i++) this.stop(same[i]);
    while (this.active.length >= GLOBAL_CAP) this.stop(this.active[0]);
  }

  private stop(v: Voice, immediate = false): void {
    const i = this.active.indexOf(v);
    if (i >= 0) this.active.splice(i, 1);
    try {
      if (immediate) { v.src.stop(); this.reap(v); }
      else {
        // Stealing a playing voice at an arbitrary sample produces a click.
        // Free its logical slot now; let a short audio-thread ramp finish it.
        const now = this.engine.ctx.currentTime;
        v.out.gain.cancelScheduledValues(now);
        v.out.gain.setValueAtTime(v.out.gain.value, now);
        v.out.gain.linearRampToValueAtTime(0, now + .008);
        this.retiring.add(v); v.src.stop(now + .009);
      }
    } catch {
      this.reap(v);
    }
  }

  private reap(v: Voice): void {
    const i = this.active.indexOf(v);
    if (i >= 0) this.active.splice(i, 1);
    this.retiring.delete(v);
    try {
      v.out.disconnect();
      v.src.disconnect(); v.panner?.disconnect();
    } catch {
      /* already disconnected */
    }
  }

  /** Hard stop everything (mode teardown / page hide). */
  stopAll(): void {
    for (const v of [...this.active, ...this.retiring]) this.stop(v, true);
  }

  stopPrefix(prefix: string): void {
    for (const v of [...this.active]) if (v.id.startsWith(prefix)) this.stop(v);
  }
}

/** Set a panner position via AudioParams when available, else the legacy API. */
export function setPannerPos(p: PannerNode, pos: Vec3): void {
  if (p.positionX) {
    p.positionX.value = pos.x;
    p.positionY.value = pos.y;
    p.positionZ.value = pos.z;
  } else {
    // Deprecated but still needed by some Safari builds.
    (p as unknown as { setPosition(x: number, y: number, z: number): void }).setPosition(pos.x, pos.y, pos.z);
  }
}
